import { useState } from "preact/hooks";
import { check, whatsapp as whatsappIcon } from "../icons.ts";
import { ApiError, submitLead, track } from "../lib/api.ts";
import { whatsappUrl } from "../lib/links.ts";
import type { LeadPrefill, LeadState } from "../lib/session.ts";
import type { WidgetConfig } from "../lib/types.ts";
import { Icon } from "./Icon.tsx";
import styles from "./LeadCard.module.css";

interface Props {
  config: WidgetConfig;
  state: LeadState;
  sessionId: string;
  conversationId: string | null;
  /** What the visitor already typed in the chat (a phone, an e-mail, the request). */
  prefill?: LeadPrefill;
  onState(state: LeadState): void;
}

type Errors = Partial<Record<"contact" | "email" | "phone" | "form", string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** "Leave your contact" offered inline after a few questions (features.leadCapture). */
export function LeadCard({ config, state, sessionId, conversationId, prefill, onState }: Props) {
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const shop = config.mode === "shop";
  const who = shop ? "obsługa sklepu" : "biuro sprzedaży";
  const number = config.widget.whatsapp && config.widget.whatsapp.replace(/\D/g, "").length >= 10 ? config.widget.whatsapp : null;
  const whatsApp = number && (
    <WhatsApp number={number} message={prefill?.message} onOpen={() => track("whatsapp_clicked", sessionId, { from: state }, conversationId)} />
  );

  if (state === "sent") {
    return (
      <div class={styles.card} role="status">
        <p class={styles.done}>
          <Icon svg={check} />
          Dziękujemy! {cap(who)} {config.tenant_name} odezwie się wkrótce.
        </p>
      </div>
    );
  }

  if (state === "offered") {
    return (
      <div class={styles.card}>
        <p class={styles.question}>Chcesz, żeby {who} {shop ? "skontaktowała" : "skontaktowało"} się z Tobą?</p>
        <div class={styles.row}>
          <button type="button" class={styles.primary} onClick={() => onState("open")}>
            Zostaw kontakt
          </button>
          <button type="button" class={styles.ghost} onClick={() => onState("dismissed")}>
            Nie teraz
          </button>
        </div>
        {whatsApp}
      </div>
    );
  }

  if (state !== "open") return null;

  async function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const value = (name: string) => String(form.get(name) ?? "").trim();
    const lead = { name: value("name"), email: value("email"), phone: value("phone").replace(/[^\d+]/g, ""), message: value("message") };
    const next: Errors = {};
    if (!lead.email && !lead.phone) next.contact = "Podaj telefon albo e-mail, żebyśmy mogli odpowiedzieć.";
    if (lead.email && !EMAIL.test(lead.email)) next.email = "Sprawdź adres e-mail.";
    if (lead.phone && lead.phone.replace(/\D/g, "").length < 9) next.phone = "Numer telefonu ma za mało cyfr.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSending(true);
    try {
      await submitLead({
        ...(lead.name ? { name: lead.name } : {}),
        ...(lead.email ? { email: lead.email } : {}),
        ...(lead.phone ? { phone: lead.phone } : {}),
        ...(lead.message ? { message: lead.message } : {}),
        session_id: sessionId,
        conversation_id: conversationId,
      });
      track("lead_captured", sessionId, {}, conversationId);
      onState("sent");
    } catch (error) {
      setErrors({ form: error instanceof ApiError ? error.message : "Nie udało się wysłać. Spróbuj ponownie." });
    } finally {
      setSending(false);
    }
  }

  return (
    <form class={styles.card} onSubmit={onSubmit} noValidate aria-labelledby="lead-title">
      <p id="lead-title" class={styles.question}>
        Zostaw kontakt, a {who} się odezwie
      </p>
      <label class={styles.field}>
        <span>Imię</span>
        <input name="name" autoComplete="name" maxLength={100} enterkeyhint="next" />
      </label>
      <label class={styles.field}>
        <span>Telefon</span>
        <input name="phone" type="tel" autoComplete="tel" inputMode="tel" maxLength={20} enterkeyhint="next" defaultValue={prefill?.phone} aria-invalid={!!errors.phone || !!errors.contact} />
        {errors.phone && <small class={styles.error}>{errors.phone}</small>}
      </label>
      <label class={styles.field}>
        <span>E-mail</span>
        <input name="email" type="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" maxLength={200} enterkeyhint="next" defaultValue={prefill?.email} aria-invalid={!!errors.email || !!errors.contact} />
        {errors.email && <small class={styles.error}>{errors.email}</small>}
      </label>
      <label class={styles.field}>
        <span>Wiadomość (opcjonalnie)</span>
        <textarea name="message" rows={prefill?.message ? 3 : 2} maxLength={2000} defaultValue={prefill?.message} />
      </label>
      {(errors.contact || errors.form) && (
        <p class={styles.error} role="alert">
          {errors.contact ?? errors.form}
        </p>
      )}
      <p class={styles.privacy}>
        Przekażemy te dane tylko firmie {config.tenant_name}, żeby mogła się z Tobą skontaktować w sprawie tej rozmowy.
      </p>
      <div class={styles.row}>
        <button type="submit" class={styles.primary} disabled={sending}>
          {sending ? "Wysyłanie…" : "Wyślij kontakt"}
        </button>
        <button type="button" class={styles.ghost} onClick={() => onState("dismissed")}>
          Anuluj
        </button>
      </div>
      {whatsApp}
    </form>
  );
}

const WHATSAPP_INTRO = "Dzień dobry, piszę z czatu na stronie.";
const whatsappText = (message?: string) => (message?.trim() ? `${WHATSAPP_INTRO} ${message.trim()}` : WHATSAPP_INTRO);

/** The office's WhatsApp with the request already written; a message typed in the form wins. */
function WhatsApp({ number, message, onOpen }: { number: string; message?: string; onOpen(): void }) {
  return (
    <a
      class={styles.whatsapp}
      href={whatsappUrl(number, whatsappText(message))}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        const form = event.currentTarget.closest("form");
        const typed = form ? String(new FormData(form).get("message") ?? "") : message;
        event.currentTarget.href = whatsappUrl(number, whatsappText(typed));
        onOpen();
      }}
    >
      <Icon svg={whatsappIcon} />
      Napisz na WhatsApp
    </a>
  );
}

const cap = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
